import { useCallback, useState } from "react";

/**
 * How fast the narration is played back.
 *
 * Kept on the device rather than on the account, like text size and theme: it
 * is a fact about the listener's ears and the moment, not about the book, and
 * syncing it would carry a commute's 1.5× into a bedtime reading.
 *
 * Deliberately a short list of named speeds rather than a slider. A slider on
 * a phone hands you 1.37×, which nobody wants and nobody can get back off.
 */
export const SPEEDS = [0.5, 0.75, 1, 1.5, 2] as const;

export const DEFAULT_SPEED = 1;

const KEY = "sageleaf.speed";

/**
 * parseSpeed reads a stored speed, falling back to normal.
 *
 * Only the speeds on the list are accepted. Storage outlives the code that
 * wrote it, and a value taken out of the list — or edited by hand — must land
 * on 1× rather than on a rate the buttons cannot show as chosen.
 */
export function parseSpeed(raw: string | null): number {
  const value = Number(raw);
  return (SPEEDS as readonly number[]).includes(value) ? value : DEFAULT_SPEED;
}

/** useSpeed holds the chosen speed and remembers it on this device. */
export function useSpeed(): [number, (speed: number) => void] {
  const [speed, setSpeed] = useState(() => {
    try {
      return parseSpeed(localStorage.getItem(KEY));
    } catch {
      return DEFAULT_SPEED;
    }
  });
  const choose = useCallback((next: number) => {
    setSpeed(parseSpeed(String(next)));
    try {
      localStorage.setItem(KEY, String(next));
    } catch {
      // Private browsing or full storage: the choice lasts this visit.
    }
  }, []);
  return [speed, choose];
}

/**
 * label is a speed as the button shows it: "1×", "1.5×", "0.75×".
 *
 * Latin digits and the multiplication sign, in both interface languages, and
 * the button is inside the transport's `dir="ltr"` block. A playback rate is
 * read the way a number on a dial is read; putting it in Persian digits made
 * the transport's one changing readout the only thing on the bar that moved
 * when the language did.
 */
export function label(speed: number): string {
  return `${speed}×`;
}
